import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';

import {
  baixarArquivoDaImportacao,
  buscarImportacao,
  type IRegistroDaImportacao,
  importarDoAcessorias,
  listarImportacoes,
} from '../../../../../hooks/useFechamentoContabilMensal';
import { numero } from '../regrasDaCarteira';
import { tamanho, totaisDo } from '../regrasDaImportacao';
import { dataEHora } from '../regrasDoFechamento';
import './importacao.css';
import { mensagemDoErro, PainelDaImportacao } from './PainelDaImportacao';

/** Evento que abre a importação: duplo clique no selo do calendário (só administradores). */
export const ABRIR_IMPORTACAO_DO_ACESSORIAS = 'fcm:importar-acessorias';

type Aba = 'nova' | 'historico';

function Historico({
  lista,
  carregando,
  onVer,
}: {
  lista: IRegistroDaImportacao[];
  carregando: boolean;
  onVer: (id: number) => void;
}) {
  const toast = useToast();
  const baixar = async (r: IRegistroDaImportacao) => {
    try {
      await baixarArquivoDaImportacao(r.id, r.arquivo_nome);
    } catch {
      toast({ status: 'error', title: 'Não foi possível baixar o arquivo', isClosable: true });
    }
  };
  if (carregando) return <div className="impCarregando">Carregando as importações…</div>;
  if (!lista.length) {
    return <div className="impVazio">Nenhuma importação feita ainda.</div>;
  }
  return (
    <div className="impTabela" style={{ maxHeight: 'none' }}>
      <table>
        <thead>
          <tr>
            <th className="num">Nº</th>
            <th>Quando</th>
            <th>Quem importou</th>
            <th>Arquivo</th>
            <th className="num">Fechadas</th>
            <th className="num">Pelo sistema</th>
            <th className="num">De fora</th>
            <th>Situação</th>
            <th aria-label="Ações" />
          </tr>
        </thead>
        <tbody>
          {lista.map(r => {
            const t = totaisDo(r);
            return (
              <tr key={r.id}>
                <td className="num cod">{r.id}</td>
                <td className="nw">{dataEHora(r.importado_em)}</td>
                <td>
                  <b>{r.importado_por_nome || r.importado_por}</b>
                  <div className="impKpiDet">{r.importado_por}</div>
                </td>
                <td>
                  <button
                    type="button"
                    className="impLink"
                    onClick={() => baixar(r)}
                    title="Baixar o arquivo original"
                  >
                    {r.arquivo_nome}
                  </button>
                  <div className="impKpiDet">{tamanho(r.arquivo_tamanho)}</div>
                </td>
                <td className="num">
                  <b>{numero(t.fechadas)}</b>
                </td>
                <td className="num">{numero(t.fechadas_pelo_sistema)}</td>
                <td className="num">{numero(t.ignoradas)}</td>
                <td>
                  {r.cancelada_em ? (
                    <>
                      <span className="impChip canc">Cancelada</span>
                      <div className="impKpiDet">
                        {r.cancelada_por_nome || r.cancelada_por} · {dataEHora(r.cancelada_em)}
                      </div>
                      <div className="impKpiDet" title={r.cancelada_motivo ?? ''}>
                        {r.cancelada_motivo}
                      </div>
                    </>
                  ) : (
                    <span className="impChip ok">Ativa</span>
                  )}
                </td>
                <td className="nw">
                  <button type="button" className="impBtn" onClick={() => onVer(r.id)}>
                    Ver detalhe
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Importa o CSV de entregas do Acessórias (S3D): o que a área entregou lá fecha aqui como
 * fechado pela área. Cada importação fica registrada (quem, quando, o arquivo e o que
 * fez, com o antes e o depois) e pode ser cancelada.
 *
 * Não tem botão na tela (decisão de 04/11/2026): fica montada no calendário e abre pelo
 * evento `ABRIR_IMPORTACAO_DO_ACESSORIAS`.
 */
export function ImportarDoAcessorias() {
  const toast = useToast();
  const cache = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [aba, setAba] = useState<Aba>('nova');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<IRegistroDaImportacao | null>(null);
  const [lista, setLista] = useState<IRegistroDaImportacao[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [detalhe, setDetalhe] = useState<IRegistroDaImportacao | null>(null);

  useEffect(() => {
    const abrir = () => {
      setAberto(true);
    };
    window.addEventListener(ABRIR_IMPORTACAO_DO_ACESSORIAS, abrir);
    return () => window.removeEventListener(ABRIR_IMPORTACAO_DO_ACESSORIAS, abrir);
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      setLista(await listarImportacoes());
    } catch (erro) {
      toast({
        status: 'error',
        title: 'Não foi possível carregar as importações',
        description: mensagemDoErro(erro),
        isClosable: true,
      });
    } finally {
      setCarregando(false);
    }
  }, [toast]);

  useEffect(() => {
    if (aberto && aba === 'historico' && !detalhe) carregar();
  }, [aberto, aba, detalhe, carregar]);

  const fechar = () => {
    setAberto(false);
    setAba('nova');
    setArquivo(null);
    setResultado(null);
    setDetalhe(null);
  };

  const atualizarCalendario = () =>
    cache.invalidateQueries({ queryKey: ['fechamento-contabil-mensal'] });

  const importar = async () => {
    if (!arquivo) return;
    setEnviando(true);
    try {
      setResultado(await importarDoAcessorias(arquivo));
      atualizarCalendario();
    } catch (erro) {
      toast({
        status: 'error',
        title: 'Não foi possível importar o arquivo',
        description: mensagemDoErro(erro),
        isClosable: true,
      });
    } finally {
      setEnviando(false);
    }
  };

  const ver = async (id: number) => {
    setCarregando(true);
    try {
      setDetalhe(await buscarImportacao(id));
    } catch (erro) {
      toast({
        status: 'error',
        title: 'Não foi possível abrir a importação',
        description: mensagemDoErro(erro),
        isClosable: true,
      });
    } finally {
      setCarregando(false);
    }
  };

  const atualizado = (r: IRegistroDaImportacao) => {
    if (resultado?.id === r.id) setResultado(r);
    if (detalhe?.id === r.id) setDetalhe(r);
    setLista(l => l.map(x => (x.id === r.id ? { ...x, ...r } : x)));
    atualizarCalendario();
  };

  const escolher = (f: File | null | undefined) => {
    if (f) setArquivo(f);
  };

  return (
    <Modal isOpen={aberto} onClose={fechar} size="6xl" scrollBehavior="inside" isCentered>
      <ModalOverlay />
      <ModalContent className="fcmImp" maxH="90vh">
        <div className="impCab">
          <div className="impTitulo">
            <h2>Importação de fechamentos do Acessórias</h2>
            <span>Entregas do S3D que fecham no sistema como fechadas pela área</span>
          </div>
          <div className="impAbas" role="tablist" aria-label="Importação">
            {(
              [
                ['nova', 'Nova importação'],
                ['historico', 'Histórico de importações'],
              ] as [Aba, string][]
            ).map(([chave, rotulo]) => (
              <button
                key={chave}
                type="button"
                role="tab"
                aria-selected={aba === chave}
                className={`impAba${aba === chave ? ' on' : ''}`}
                onClick={() => {
                  setAba(chave);
                  setDetalhe(null);
                }}
              >
                {rotulo}
              </button>
            ))}
          </div>
        </div>
        <ModalCloseButton />
        <ModalBody p={0} className="impCorpo">
          {aba === 'nova' && !resultado && (
            <>
              <label
                htmlFor="fcm-importacao-arquivo"
                className={`impArquivo${arrastando ? ' arrastando' : ''}`}
                onDragOver={e => {
                  e.preventDefault();
                  setArrastando(true);
                }}
                onDragLeave={() => setArrastando(false)}
                onDrop={e => {
                  e.preventDefault();
                  setArrastando(false);
                  escolher(e.dataTransfer.files?.[0]);
                }}
              >
                <input
                  id="fcm-importacao-arquivo"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={e => escolher(e.target.files?.[0])}
                  aria-label="Arquivo CSV de entregas do Acessórias"
                />
                <span className="impArqIcone" aria-hidden="true">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 16V4M7 9l5-5 5 5M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
                  </svg>
                </span>
                <span className="impArqTexto">
                  {arquivo ? (
                    <>
                      <b>{arquivo.name}</b>
                      <span>{tamanho(arquivo.size)} · clique para trocar o arquivo</span>
                    </>
                  ) : (
                    <>
                      <b>Escolha ou arraste o CSV do S3D</b>
                      <span>
                        Export de “Gestão de entregas”, separado por ponto e vírgula, até 5 MB
                      </span>
                    </>
                  )}
                </span>
              </label>
              <div className="impSecao">
                <span className="impSub">Como funciona</span>
                <ul className="impRegras">
                  <li>
                    Cada competência entregue de <b>Fechamento contábil</b> que está no total do mês
                    fecha como fechada pela área, mesmo com pendência (fica marcada).
                  </li>
                  <li>
                    A empresa é achada pelo CNPJ. O que não está no total, não tem revisão ou foi
                    reaberto pela área fica de fora, com o motivo.
                  </li>
                  <li>
                    Fica registrado quem importou, quando e o arquivo usado. A importação pode ser
                    cancelada depois no histórico.
                  </li>
                  <li>Importar o mesmo arquivo de novo não duplica.</li>
                </ul>
              </div>
              <div className="impAcoes fim">
                <button
                  type="button"
                  className="impBtn cheio"
                  onClick={importar}
                  disabled={!arquivo || enviando}
                >
                  {enviando ? 'Importando… (pode levar alguns segundos)' : 'Importar'}
                </button>
              </div>
            </>
          )}

          {aba === 'nova' && resultado && (
            <>
              <PainelDaImportacao registro={resultado} onAtualizado={atualizado} />
              <div className="impAcoes fim">
                <button
                  type="button"
                  className="impBtn leve"
                  onClick={() => {
                    setResultado(null);
                    setArquivo(null);
                  }}
                >
                  Importar outro arquivo
                </button>
              </div>
            </>
          )}

          {aba === 'historico' && !detalhe && (
            <Historico lista={lista} carregando={carregando} onVer={ver} />
          )}

          {aba === 'historico' && detalhe && (
            <>
              <div className="impAcoes">
                <button type="button" className="impBtn leve" onClick={() => setDetalhe(null)}>
                  ← Voltar ao histórico
                </button>
              </div>
              <PainelDaImportacao registro={detalhe} onAtualizado={atualizado} />
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
